import { useState, useEffect, useCallback } from 'react';
import { useAuthContext } from '@/hooks/useAuthContext';
import { useStopImpersonation } from '@/hooks/useImpersonation';
import { formatRole } from '@/lib/utils';

/**
 * Calculates remaining minutes from an ISO expiration timestamp.
 * Returns 0 if already expired.
 */
function getRemainingMinutes(expiresAt: string): number {
  const diff = new Date(expiresAt).getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / 60_000));
}

/**
 * Persistent amber banner displayed at the top of the page during an active
 * impersonation session. Shows admin identity, target user details, a
 * countdown timer, and a keyboard-accessible "Stop Impersonating" button.
 *
 * Requirements: 4.1, 4.2, 4.3, 4.4, 4.5, 3.3
 */
export default function ImpersonationBanner() {
  const { isImpersonating, realUser, user } = useAuthContext();
  const { mutate: stopImpersonation, isLoading: isStopping } = useStopImpersonation();

  // Read expiresAt from localStorage since AuthContext doesn't expose it directly
  const getExpiresAt = useCallback((): string | null => {
    try {
      const raw = localStorage.getItem('impersonation_state');
      if (!raw) return null;
      const state = JSON.parse(raw);
      return state.expiresAt ?? null;
    } catch {
      return null;
    }
  }, []);

  const [remainingMinutes, setRemainingMinutes] = useState<number>(() => {
    const expiresAt = getExpiresAt();
    return expiresAt ? getRemainingMinutes(expiresAt) : 0;
  });

  // Countdown timer — updates every 15 seconds for a smooth UX without
  // excessive re-renders.
  useEffect(() => {
    if (!isImpersonating) return;

    const tick = () => {
      const expiresAt = getExpiresAt();
      if (expiresAt) {
        setRemainingMinutes(getRemainingMinutes(expiresAt));
      }
    };

    tick(); // sync immediately on mount / state change
    const id = setInterval(tick, 15_000);
    return () => clearInterval(id);
  }, [isImpersonating, getExpiresAt]);

  if (!isImpersonating || !realUser || !user) return null;

  const adminName = `${realUser.firstName} ${realUser.lastName}`;
  const targetName = `${user.firstName} ${user.lastName}`;

  return (
    <div
      role="status"
      aria-live="polite"
      className="bg-amber-500 text-amber-950 px-4 py-2"
    >
      <div className="max-w-[1600px] mx-auto flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm font-medium">
          <span>
            You (<span data-testid="admin-name">{adminName}</span>) are viewing as{' '}
            <span data-testid="target-name">{targetName}</span>
          </span>
          <span data-testid="target-email" className="opacity-80">
            {user.email}
          </span>
          <span
            data-testid="target-role"
            className="rounded bg-amber-600/30 px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wide"
          >
            {formatRole(user.role)}
          </span>
          <span data-testid="remaining-time" className="tabular-nums">
            {remainingMinutes} min remaining
          </span>
        </div>

        <button
          type="button"
          onClick={() => stopImpersonation()}
          disabled={isStopping}
          className="rounded bg-amber-800 px-3 py-1 text-sm font-semibold text-white transition-colors hover:bg-amber-900 focus:outline-none focus:ring-2 focus:ring-amber-300 focus:ring-offset-1 disabled:opacity-50"
        >
          {isStopping ? 'Stopping…' : 'Stop Impersonating'}
        </button>
      </div>
    </div>
  );
}
